"use client"

import { useState, useEffect } from "react"
import { collection, query, orderBy, getDocs, where } from "firebase/firestore"
import { db } from "@/lib/firebase"
import { Loader2, Search, Calendar, User, Heart, ArrowRight } from "lucide-react"
import { Link, useNavigate } from "react-router-dom"
import { cn } from "@/lib/utils"

export default function CurrentAffairsPage() {
  const [articles, setArticles] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const navigate = useNavigate()

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const q = query(collection(db, "articles"), orderBy("createdAt", "desc"))
        const snapshot = await getDocs(q)
        const allArticles = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        setArticles(allArticles.filter((a: any) => a.isVisible !== false))
      } catch (error) {
        console.error("Error fetching articles:", error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchArticles()
  }, [])

  const filteredArticles = articles.filter(article => 
    article.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.category?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center space-y-4">
        <Loader2 className="w-8 h-8 text-[#b48c36] animate-spin" />
        <p className="text-[#b48c36] font-bold text-[10px] uppercase tracking-[0.4em]">Loading Journal...</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50/50 pb-24">
      {/* Search Header */}
      <div className="bg-white border-b border-slate-100 py-12 px-6">
        <div className="max-w-7xl mx-auto flex flex-col items-center space-y-8">
           <div className="text-center space-y-2">
              <h1 className="text-4xl font-serif font-bold text-slate-950">Current Affairs</h1>
              <p className="text-slate-500 text-sm">In-depth analysis for Civil Service aspirants.</p>
           </div>
           
           <div className="relative w-full max-w-2xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input 
                type="text" 
                placeholder="Search articles..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-full py-4 pl-12 pr-6 focus:outline-none focus:border-[#b48c36] transition-colors shadow-sm"
              />
           </div>
        </div>
      </div>

      {/* Articles Grid */}
      <div className="max-w-7xl mx-auto px-6 py-12">
         {filteredArticles.length === 0 ? (
           <div className="text-center py-24">
              <p className="text-slate-500 font-serif text-lg">No matches found for your query.</p>
           </div>
         ) : (
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article) => (
                <div key={article.id} className="bg-white rounded-xl shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07)] overflow-hidden group hover:shadow-xl transition-shadow duration-300 flex flex-col">
                   {/* Card Media */}
                   <div className="relative aspect-video overflow-hidden bg-slate-100">
                      {article.image ? (
                        <img 
                          src={article.image} 
                          alt={article.title} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                        />
                      ) : (
                        <div className="w-full h-full bg-slate-100 flex items-center justify-center text-slate-300 font-bold uppercase tracking-widest text-[10px]">Rohith's Academy</div>
                      )}
                      
                      {/* Floating UI */}
                      <div className="absolute top-4 left-4">
                         <span className="bg-[#b48c36] text-white text-[9px] font-bold px-3 py-1 rounded-md uppercase tracking-wider shadow-lg">
                            {article.category}
                         </span>
                      </div>
                      <button className="absolute top-4 right-4 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-slate-400 hover:text-[#b48c36] transition-colors shadow-md">
                         <Heart size={16} />
                      </button>
                   </div>

                   {/* Card Content */}
                   <div className="p-6 flex-1 flex flex-col">
                      <div className="flex items-center gap-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">
                         <div className="flex items-center gap-1.5"><Calendar size={12}/> {article.createdAt?.toDate ? new Date(article.createdAt.toDate()).toLocaleDateString() : 'Recent'}</div>
                         <div className="flex items-center gap-1.5"><User size={12}/> Team HIAS</div>
                      </div>
                      
                      <h3 className="text-xl font-serif font-bold text-slate-950 mb-6 line-clamp-2 leading-snug group-hover:text-[#b48c36] transition-colors">
                        {article.title}
                      </h3>
                      
                      <div className="mt-auto pt-6 border-t border-slate-50">
                         <Link 
                           to={`/article/${article.slug}`} 
                           className="text-[#b48c36] font-bold text-[11px] uppercase tracking-widest flex items-center gap-2 hover:translate-x-1 transition-transform"
                         >
                            Read Full Article <ArrowRight size={14} />
                         </Link>
                      </div>
                   </div>
                </div>
              ))}
           </div>
         )}
      </div>
    </div>
  )
}
